import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import gridHorizontalAsset from "@/assets/loader/loader-grid-horizontal.svg";
import gridVerticalAsset from "@/assets/loader/loader-grid-vertical.svg";
import watermarkAsset from "@/assets/loader/loader-brand-watermark.svg";
import { cn } from "@/lib/cn";
import { FullPageLoaderSpinner } from "./FullPageLoaderSpinner";

const loaderAssets = {
  gridHorizontal: gridHorizontalAsset,
  gridVertical: gridVerticalAsset,
  watermark: watermarkAsset
} as const;

export type FullPageLoaderProps = HTMLAttributes<HTMLDivElement> & {
  titlePrefix?: ReactNode;
  titleHighlight?: ReactNode;
  titleSuffix?: ReactNode;
  description?: ReactNode;
  fullScreen?: boolean;
  statusLabel?: string;
};

export const FullPageLoader = forwardRef<HTMLDivElement, FullPageLoaderProps>(
  (
    {
      className,
      titlePrefix = "Your account is ready. I'll take you to your ",
      titleHighlight = "dashboard",
      titleSuffix = " now.",
      description = "Your account is being configured. this takes just a moment",
      fullScreen = true,
      statusLabel = "Loading account dashboard",
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      role="status"
      aria-busy="true"
      aria-label={statusLabel}
      className={cn(
        "bfrs-relative bfrs-isolate bfrs-flex bfrs-overflow-hidden bfrs-rounded-lg bfrs-bg-surface-app bfrs-text-text-primary",
        fullScreen ? "bfrs-min-h-dvh" : "bfrs-min-h-[520px]",
        className
      )}
      {...props}
    >
      <img
        src={loaderAssets.gridHorizontal}
        alt=""
        aria-hidden="true"
        className="bfrs-pointer-events-none bfrs-absolute bfrs-left-[3%] bfrs-top-0 bfrs-z-0 bfrs-h-[95%] bfrs-w-[92%] bfrs-max-w-none bfrs-select-none bfrs-object-fill"
      />
      <img
        src={loaderAssets.gridVertical}
        alt=""
        aria-hidden="true"
        className="bfrs-pointer-events-none bfrs-absolute bfrs-left-0 bfrs-top-[3.5%] bfrs-z-0 bfrs-h-[98%] bfrs-w-full bfrs-max-w-none bfrs-select-none bfrs-object-fill"
      />
      <img
        src={loaderAssets.watermark}
        alt=""
        aria-hidden="true"
        className="bfrs-pointer-events-none bfrs-absolute -bfrs-bottom-[52%] -bfrs-left-[5%] bfrs-z-0 bfrs-h-[113%] bfrs-w-[68%] bfrs-max-w-none bfrs-select-none bfrs-object-fill"
      />

      <div className="bfrs-relative bfrs-z-10 bfrs-mx-auto bfrs-flex bfrs-w-full bfrs-max-w-4xl bfrs-flex-col bfrs-items-center bfrs-justify-center bfrs-px-5 bfrs-py-20 bfrs-text-center">
        <FullPageLoaderSpinner />
        <h1 className="bfrs-max-w-[720px] bfrs-text-balance bfrs-text-3xl bfrs-font-bold bfrs-leading-[1.12] bfrs-tracking-normal sm:bfrs-text-4xl">
          {titlePrefix}
          <span className="bfrs-text-primary-bright">{titleHighlight}</span>
          {titleSuffix}
        </h1>
        {description && <p className="bfrs-mt-4 bfrs-text-base bfrs-leading-7 bfrs-text-text-primary sm:bfrs-text-xl">{description}</p>}
      </div>
    </div>
  )
);

FullPageLoader.displayName = "FullPageLoader";
